import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  emptyBoard,
  localPitBoardStore,
  PIT_COLUMNS,
  PitBoardStore,
  STORAGE_KEY,
} from './pitBoardStore';

import {
  KartRating,
  PitBoard,
  PitColumn,
  PitSlot,
  PitSlotOccupant,
  TeamInfo,
} from '../types';
import { parseLapTimeSeconds } from '../analytics/lapTime';
import { apexPitSeconds } from '../analytics/elapsed';

// Storage lives in `pitBoardStore` now; re-exported so callers and tests that
// know these by their original home keep working.
export {
  emptyBoard,
  migrateV1,
  parseStoredBoard,
  PIT_COLUMNS,
  STORAGE_KEY,
  STORAGE_VERSION,
} from './pitBoardStore';


const calculateKartRating = (
  averageTime: string,
  allTeams: Map<string, TeamInfo>,
): KartRating => {
  if (!averageTime || averageTime === '-') return 'unknown';

  const validAverageTimes: number[] = [];
  allTeams.forEach(team => {
    const avgTime = team.getAverageLapTime();
    if (avgTime && avgTime !== '-') {
      const timeInSeconds = parseLapTimeSeconds(avgTime);
      if (timeInSeconds !== null) {
        validAverageTimes.push(timeInSeconds);
      }
    }
  });

  if (validAverageTimes.length === 0) return 'unknown';

  // Sort times (fastest first)
  validAverageTimes.sort((a, b) => a - b);

  const currentTimeInSeconds = parseLapTimeSeconds(averageTime);
  if (currentTimeInSeconds === null) return 'unknown';

  const fastThreshold = validAverageTimes[Math.floor(validAverageTimes.length * 0.33)];
  const mediumThreshold = validAverageTimes[Math.floor(validAverageTimes.length * 0.66)];

  if (currentTimeInSeconds <= fastThreshold) return 'fast';
  if (currentTimeInSeconds <= mediumThreshold) return 'medium';
  return 'slow';
};

// ── Pure board operations ─────────────────────────────────────────────────────
// Kept outside the hook so the rules can be tested without rendering, and so every
// caller shares one definition of what "this team's slot" means.

export function findSlotByTeam(
  board: PitBoard,
  teamId: string,
): { column: PitColumn; slot: PitSlot } | null {
  for (const column of PIT_COLUMNS) {
    const slot = board[column].find(s => s.occupant?.teamId === teamId);
    if (slot) return { column, slot };
  }
  return null;
}

/** Frees whichever box the team holds, leaving the kart itself on the board. */
export function vacateTeam(board: PitBoard, teamId: string): PitBoard {
  const found = findSlotByTeam(board, teamId);
  if (!found) return board;

  return {
    ...board,
    [found.column]: board[found.column].map(s =>
      s.slotId === found.slot.slotId
        ? { ...s, occupant: undefined, lastOccupant: s.occupant }
        : s,
    ),
  };
}

/** Takes the box off the board entirely, occupied or not. */
export function removeSlot(board: PitBoard, slotId: string): PitBoard {
  const next = { ...board };
  let changed = false;

  for (const column of PIT_COLUMNS) {
    const filtered = board[column].filter(s => s.slotId !== slotId);
    if (filtered.length !== board[column].length) changed = true;
    next[column] = filtered;
  }
  return changed ? next : board;
}

/**
 * Moves a whole box to another column — the kart, its rating and whoever is
 * standing in it travel together.
 *
 * Deliberately not `assignKartToColumn`: that models a kart *swap*, so it
 * leaves an empty box behind in the source column and mints a second one in the
 * destination. Correcting a mis-tap that way would litter the board.
 */
export function moveSlot(
  board: PitBoard,
  slotId: string,
  to: PitColumn,
): PitBoard {
  const from = PIT_COLUMNS.find(c => board[c].some(s => s.slotId === slotId));
  if (!from || from === to) return board;

  const slot = board[from].find(s => s.slotId === slotId)!;
  return {
    ...board,
    [from]: board[from].filter(s => s.slotId !== slotId),
    // `column` is denormalised onto the slot as well as being the board key.
    // Leaving it stale makes a slot claim a column it is not in.
    [to]: [...board[to], { ...slot, column: to }],
  };
}

export function occupySlot(
  board: PitBoard,
  column: PitColumn,
  slotId: string,
  occupant: PitSlotOccupant,
): PitBoard {
  return {
    ...board,
    [column]: board[column].map(s => (s.slotId === slotId ? { ...s, occupant } : s)),
  };
}

/** Sets the pace rating on one box; the same board back when nothing changed. */
export function rateSlot(board: PitBoard, slotId: string, rating: KartRating): PitBoard {
  for (const column of PIT_COLUMNS) {
    const slot = board[column].find(s => s.slotId === slotId);
    if (!slot) continue;
    if (slot.rating === rating) return board;
    return {
      ...board,
      [column]: board[column].map(s => (s.slotId === slotId ? { ...s, rating } : s)),
    };
  }
  return board;
}

/** Removes the box a team is standing in — what happens when they leave the pit. */
export function removeTeamSlot(board: PitBoard, teamId: string): PitBoard {
  const found = findSlotByTeam(board, teamId);
  return found ? removeSlot(board, found.slot.slotId) : board;
}

function syncColumnDrivers(slots: PitSlot[], teams: Map<string, TeamInfo>): PitSlot[] {
  const next = slots.map(slot => {
    if (!slot.occupant) return slot;
    const team = teams.get(slot.occupant.teamId);
    if (!team || team.driver === slot.occupant.driver) return slot;

    return { ...slot, occupant: { ...slot.occupant, driver: team.driver } };
  });
  // Returning the original array when nothing moved keeps React from re-rendering.
  return next.every((slot, i) => slot === slots[i]) ? slots : next;
}

/** Follows driver changes within a stint; the kart in the box is left alone. */
export function syncOccupantDrivers(
  board: PitBoard,
  teams: Map<string, TeamInfo>,
): PitBoard {
  const red = syncColumnDrivers(board.red, teams);
  const green = syncColumnDrivers(board.green, teams);
  const blue = syncColumnDrivers(board.blue, teams);

  return red === board.red && green === board.green && blue === board.blue
    ? board
    : { red, green, blue };
}

/**
 * How long a kart has been standing in the pit, in seconds.
 *
 * Apex's own stopwatch where it is sending one, our timing of the stop
 * otherwise, and 0 when we know neither — an unknown wait sorts last rather
 * than jumping the queue.
 */
export function pitWaitSeconds(team: TeamInfo, now: number): number {
  const apex = apexPitSeconds(team.onTrackTime);
  if (apex != null) return apex;
  if (team.pitEntryTime != null) return Math.max(0, (now - team.pitEntryTime) / 1000);
  return 0;
}

// ── Persistence ───────────────────────────────────────────────────────────────


/**
 * Converts a board saved by the previous model. Blank karts become empty slots,
 * temporarily-occupied blanks and plain assignments both become occupied slots, so
 * nothing a user had arranged is lost on upgrade.
 */


// ── Hook ──────────────────────────────────────────────────────────────────────

export interface PitAssignmentOptions {
  /**
   * Source of "now" for slot timestamps. Replay passes its virtual clock so stamps
   * read as the time in the race rather than the time you watched it.
   */
  now?: () => number;
  /**
   * Where the board persists, when no `store` is given. Replay uses a per-event
   * key so reviewing an old race cannot overwrite the pit plan for a race
   * running right now.
   */
  storageKey?: string;
  /**
   * Overrides where the board lives entirely. The live screen passes a
   * server-backed store so everyone on the pit wall shares one board; leaving it
   * out keeps the original per-browser behaviour.
   *
   * Must be stable across renders — a new object each render reloads the board.
   */
  store?: PitBoardStore;
}

export const usePitAssignments = (
  teams: Map<string, TeamInfo>,
  options: PitAssignmentOptions = {},
) => {
  const storageKey = options.storageKey ?? STORAGE_KEY;
  const providedStore = options.store;
  const store = useMemo(
    () => providedStore ?? localPitBoardStore(storageKey),
    [providedStore, storageKey],
  );

  // The key travels with the board so the save effect can tell "not loaded yet" from
  // "loaded and empty". Without it, mounting writes an empty board over stored data,
  // and switching keys writes the previous event's board into the new slot.
  //
  // `local` says whether we are the ones who changed it. A board that arrived
  // from the server — loaded on mount, or pushed because somebody else moved a
  // kart — must not be written straight back: two screens would then save each
  // other's boards in a loop, every save bumping the version and making the
  // other side's next write a conflict. That is what "not synced" looks like.
  const [state, setState] = useState<{
    key: string | null;
    board: PitBoard;
    local: boolean;
  }>({
    key: null,
    board: emptyBoard(),
    local: false,
  });
  const board = state.board;

  // Changes made here that the server has not confirmed yet. Each is the
  // function that made it, so it can be made again on top of whatever board
  // arrives from elsewhere in the meantime. Without this, a board pushed by
  // another screen — or our own write coming back round — replaced the state
  // wholesale, and a kart assigned a moment earlier popped back into the queue
  // until its own save caught up: the flicker people saw on the wall.
  const pendingOpsRef = useRef<((board: PitBoard) => PitBoard)[]>([]);
  // The newest board, including updates React has not rendered yet, so two
  // edits in one tick build on each other rather than both on the old board.
  const boardRef = useRef<PitBoard>(state.board);

  const prevPitStatusRef = useRef<Map<string, boolean>>(new Map());

  // Held in a ref so a caller passing an inline arrow (the usual case) does not
  // invalidate every callback below on each render.
  const nowRef = useRef(options.now);
  nowRef.current = options.now;
  const clock = useCallback(
    (): number => (nowRef.current ? nowRef.current() : Date.now()),
    [],
  );

  useEffect(() => {
    let cancelled = false;
    void store
      .load()
      .then((loaded) => {
        if (cancelled) return;
        boardRef.current = loaded;
        setState({ key: store.key, board: loaded, local: false });
      })
      .catch((error: Error) => {
        console.error('Error loading the pit board:', error.message);
        // An empty board beats a stuck one; the next edit saves over it.
        if (cancelled) return;
        boardRef.current = emptyBoard();
        setState({ key: store.key, board: boardRef.current, local: false });
      });
    return () => {
      cancelled = true;
    };
  }, [store]);

  useEffect(() => {
    // Only save a board that belongs to this store. Without the key check,
    // mounting writes an empty board over stored data and switching stores
    // writes the previous event's board into the new one.
    if (state.key !== store.key) return;
    // Never echo back a board the server gave us.
    if (!state.local) return;
    // Once the server has this board it has every change up to here; only
    // what is done after this point still needs carrying.
    const carried = pendingOpsRef.current.slice();
    void store.save(state.board).then((result) => {
      if (result !== 'saved') return;
      pendingOpsRef.current = pendingOpsRef.current.filter((op) => !carried.includes(op));
    });
  }, [state, store]);

  // Somebody else on the pit wall moved a kart — or our write lost to theirs.
  // Their board is the base; anything unsaved here goes back on top of it and
  // is saved again, so nobody's kart is lost either way.
  useEffect(() => {
    if (!store.subscribe) return;
    return store.subscribe((remote) => {
      const pending = pendingOpsRef.current;
      const merged = pending.reduce((acc, op) => op(acc), remote);
      boardRef.current = merged;
      setState({ key: store.key, board: merged, local: merged !== remote });
    });
  }, [store]);

  const updateBoard = useCallback((fn: (board: PitBoard) => PitBoard) => {
    const current = boardRef.current;
    const next = fn(current);
    if (next === current) return;
    pendingOpsRef.current = [...pendingOpsRef.current, fn];
    boardRef.current = next;
    setState(prev => ({ key: prev.key, board: next, local: true }));
  }, []);

  // A kart leaving the box gives its slot up. Runs on every teams change, including
  // a replay seek, so the board always reflects the moment being shown.
  useEffect(() => {
    if (teams.size === 0) return;

    const previous = prevPitStatusRef.current;
    const exited: string[] = [];
    const current = new Map<string, boolean>();

    teams.forEach((team, teamId) => {
      current.set(teamId, team.inBox);
      if ((previous.get(teamId) ?? false) && !team.inBox) exited.push(teamId);
    });
    prevPitStatusRef.current = current;

    if (exited.length === 0) return;
    updateBoard(current => exited.reduce(removeTeamSlot, current));
  }, [teams, updateBoard]);

  // Drivers change during a stint; the kart standing in the box does not.
  useEffect(() => {
    if (teams.size === 0) return;
    updateBoard(prev => syncOccupantDrivers(prev, teams));
  }, [teams, updateBoard]);

  const assignKartToColumn = useCallback(
    (teamId: string, column: PitColumn) => {
      const team = teams.get(teamId);
      if (!team) return;

      const at = clock();
      const averageTime = team.getAverageLapTime();
      const rating = calculateKartRating(averageTime, teams);
      const occupant: PitSlotOccupant = {
        teamId,
        driver: team.driver,
        teamName: team.teamName?.trim() || undefined,
        category: team.category?.trim().toLowerCase() || undefined,
        since: at,
      };
      // Ids are fixed here, not inside the update: the update may run again on
      // top of a board from another screen, and must then find its own work
      // rather than do it twice.
      const newSlotId = `slot_${at}_${Math.random().toString(36).slice(2, 11)}`;
      const leftBehindId = `slot_${at}_${Math.random().toString(36).slice(2, 11)}`;

      updateBoard(prev => {
        if (PIT_COLUMNS.some(c => prev[c].some(s => s.slotId === newSlotId || s.slotId === leftBehindId))) {
          return prev;
        }
        // Free whatever box they held first, wherever it was. The previous model only
        // cleaned up one of the two shapes, which let a team sit in two columns.
        const vacated = vacateTeam(prev, teamId);

        const freeSlot = vacated[column]
          .filter(s => !s.occupant)
          .sort((a, b) => a.createdAt - b.createdAt)[0];

        if (!freeSlot) {
          const slot: PitSlot = {
            slotId: newSlotId,
            kartNumber: team.kartNumber,
            column,
            createdAt: at,
            rating,
            averageTime,
            origin: 'auto',
            occupant,
          };
          return { ...vacated, [column]: [...vacated[column], slot] };
        }

        // They take the waiting kart and leave the one they arrived in behind.
        const occupied = occupySlot(vacated, column, freeSlot.slotId, occupant);
        const leftBehind: PitSlot = {
          slotId: leftBehindId,
          kartNumber: team.kartNumber,
          column,
          createdAt: at,
          rating,
          averageTime,
          origin: 'auto',
          // They just climbed out of it; that is who its pace belongs to.
          lastOccupant: occupant,
        };
        return { ...occupied, [column]: [...occupied[column], leftBehind] };
      });
    },
    [teams, clock, updateBoard],
  );

  const addBlankKartToColumn = useCallback(
    (kartNumber: string, column: PitColumn) => {
      const at = clock();
      const slot: PitSlot = {
        slotId: `slot_${at}_${Math.random().toString(36).slice(2, 11)}`,
        kartNumber,
        column,
        createdAt: at,
        rating: 'unknown',
        averageTime: 'N/A',
        origin: 'manual',
      };
      updateBoard(prev =>
        prev[column].some(s => s.slotId === slot.slotId)
          ? prev
          : { ...prev, [column]: [...prev[column], slot] },
      );
    },
    [clock, updateBoard],
  );

  const removeSlotById = useCallback(
    (slotId: string) => updateBoard(prev => removeSlot(prev, slotId)),
    [updateBoard],
  );

  const moveSlotToColumn = useCallback(
    (slotId: string, to: PitColumn) => updateBoard(prev => moveSlot(prev, slotId, to)),
    [updateBoard],
  );

  // The automatic rating is a guess from lap averages, and a blank kart has no
  // laps at all. The person who just drove it knows better.
  const setSlotRating = useCallback(
    (slotId: string, rating: KartRating) => updateBoard(prev => rateSlot(prev, slotId, rating)),
    [updateBoard],
  );

  const clearColumn = useCallback(
    (column: PitColumn) => updateBoard(prev => ({ ...prev, [column]: [] })),
    [updateBoard],
  );

  const clearAllAssignments = useCallback(
    () => updateBoard(() => emptyBoard()),
    [updateBoard],
  );

  // Team ids in the order they joined the queue. A kart keeps its place until
  // it is assigned or leaves the pit; sorting by a live stopwatch instead made
  // the queue reshuffle under the engineer's finger.
  const arrivalRef = useRef<string[]>([]);

  // Teams sent away from the queue by hand: in the pit for a penalty, or a
  // stop nobody here is handling. Apex still says they are in the box, so this
  // is the only way off the list. It lasts for this stop only — once the kart
  // leaves the pit the entry is forgotten and the next stop queues as normal.
  // Kept on this screen: each tablet decides what its own operator sees.
  const [dismissed, setDismissed] = useState<string[]>([]);

  const dismissFromQueue = useCallback((teamId: string) => {
    setDismissed(current => (current.includes(teamId) ? current : [...current, teamId]));
  }, []);

  useEffect(() => {
    if (dismissed.length === 0) return;
    const gone = dismissed.filter(id => !(teams.get(id)?.inBox ?? false));
    if (gone.length > 0) setDismissed(current => current.filter(id => !gone.includes(id)));
  }, [teams, dismissed]);

  const getUnassignedPitTeams = useCallback((): TeamInfo[] => {
    const waiting = new Map<string, TeamInfo>();
    teams.forEach(team => {
      if (team.inBox && !findSlotByTeam(board, team.id) && !dismissed.includes(team.id)) {
        waiting.set(team.id, team);
      }
    });

    const kept = arrivalRef.current.filter(id => waiting.has(id));
    const known = new Set(kept);
    // Karts first seen together — the pit was already full when we joined —
    // have no arrival order of ours to go on, so the longest wait goes first.
    const at = clock();
    const newcomers = Array.from(waiting.values())
      .filter(team => !known.has(team.id))
      .sort((a, b) => pitWaitSeconds(b, at) - pitWaitSeconds(a, at));

    arrivalRef.current = [...kept, ...newcomers.map(team => team.id)];
    return arrivalRef.current.map(id => waiting.get(id)!);
  }, [teams, board, clock, dismissed]);

  /** Whether the team standing in this box is currently in the pit. */
  const getSlotPitStatus = useCallback(
    (slot: PitSlot): boolean => {
      if (!slot.occupant) return false;
      return teams.get(slot.occupant.teamId)?.inBox ?? false;
    },
    [teams],
  );

  return {
    board,
    assignKartToColumn,
    addBlankKartToColumn,
    removeSlot: removeSlotById,
    setSlotRating,
    moveSlot: moveSlotToColumn,
    clearColumn,
    clearAllAssignments,
    getUnassignedPitTeams,
    dismissFromQueue,
    getSlotPitStatus,
  };
};
